<template>
    <el-select
        filterable
        :multiple="multiple"
        remote
        :remote-method="searchUsers"
        reserve-keyword
        :placeholder="placeholder"
        value-key="id"
        v-loading="loadingUsers"
        v-model="result"
        class="user-select"
    >
        <template #prefix>
            <el-icon><User /></el-icon>
        </template>
        <template #header v-if="query.length < minimumCharacters">
            <div class="text-center">
                <el-text><el-icon class="me-2"><InfoFilled /></el-icon>{{ pleaseTypeText }}</el-text>
            </div>
        </template>
        <template #header v-else-if="fetchedUsers.length < 1">
            <div class="text-center">
                <el-text type="danger"><el-icon class="me-2"><WarningFilled /></el-icon>No matching users found</el-text>
            </div>
        </template>

        <div v-loading="loadingUsers">
            <el-option
                v-for="user in matchingUsers"
                :key="user.id"
                :label="`${user.first_name} ${user.last_name}`"
                :value="user"
            >
                <div>
                    <span style="float: left;" v-html="highlightUser(user)"></span>

                    <div style="float: right; vertical-align: middle">
                        <el-tag v-for="role in user.roles.slice(0, 1)" :key="role.id" type="info">{{ role.name }}</el-tag>
                        <el-popover v-if="user.roles.length > 1" title="User roles" placement="right">
                            <template #reference>
                                <el-tag class="ms-2" type="info">+{{ user.roles.length - 1 }} more<el-icon class="el-icon--right"><QuestionFilled /></el-icon></el-tag>
                            </template>

                            <el-text v-for="role in user.roles" :key="role.id">{{ role.name }}<br /></el-text>
                        </el-popover>
                    </div>
                </div>
            </el-option>
        </div>

    </el-select>
</template>

<script>
import api from '@/lib/api'

export default {
    name: 'SelectUser',

    emits: ['update:modelValue'],

    props: {
        modelValue: { type: Object, default: () => ({}) },
        multiple: { type: Boolean, default: false },
        placeholder: { type: String, default: 'Type to search users' },
        searchRoles: { type: Boolean, default: false },
        withRole: { type: Object, default: undefined }
    },

    computed: {
        result: {
            get () { return this.modelValue },
            set(value) { this.$emit('update:modelValue', value) }
        },

        matchingUsers () {
            if (this.multiple && this.result) {
                return this.result.concat(
                    this.fetchedUsers
                    .filter( u => !this.result.map ( user => user.id ).includes(u.id) )
                )
            } else if (this.result?.id) {
                return [ this.result ].concat(
                    this.fetchedUsers
                    .filter( u => this.result.id !== u.id )
                )
            }

            return this.fetchedUsers
        },

        pleaseTypeText () {
            const searchedFields = ['name', 'email address']
            if (this.searchRoles) { searchedFields.push('roles') }

            const withRoleText = this.withRole ? ` with the ${this.withRole.name} role` : ''

            return `Please type at least ${this.minimumCharacters} characters to start searching users${withRoleText} by ${searchedFields.slice(0, searchedFields.length-1).join(', ')} and ${searchedFields.at(-1)}.`
        }
    },

    data: () => ({
        loadingUsers: false,
        query: '',
        fetchedUsers: [],
        minimumCharacters: 3
    }),

    methods: {
        searchUsers (query, force = true) {
            let params = {
                search_roles: this.searchRoles,
                search: query.length >= this.minimumCharacters ? query : undefined,
                with_role: this.withRole?.id
            }

            if (query.length < this.minimumCharacters && !(!!this.withRole && (query !== this.query || force))) {
                return
            }

            this.query = params.search || '' // Keep last query in memory

            this.loadingUsers = true

            api.user.getUsers ( { search_roles: this.searchRoles, search: query, with_role: this.withRole?.id } )
            .then(response => {
                this.fetchedUsers = response.data.users
            })
            .catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not load users: ${err}`,
                    type: 'error'
                })
            })
            .finally (() => {this.loadingUsers = false})
        },

        highlightUser(user) {
            // const rolesText = user?.roles?.length > 0 ? ` (${user?.roles?.map(r => r.name).join(', ')})` : ''
            const text = `${user.first_name} ${user.last_name} &lt;${user.email}&gt;`

            if (!this.query) return text
            return text.replace(new RegExp(this.query, "ig"), "<strong>$&</strong>")
        }
    },

    mounted () {
        this.searchUsers('', true)
    }
}
</script>

<style>
.user-select .el-select__wrapper.is-filterable:hover,
.user-select input[type="text"]:hover {
    cursor: pointer;
}
</style>